import { useState, type FormEvent } from 'react';

import type { CustomerDetail } from '@domain/customer/Customer';
import type { CustomerEditableFields } from '@domain/customer/CustomerRepository';
import {
  InvalidCustomerEdit,
  editCustomer,
} from '@application/customer/editCustomer';
import { apiClient } from '@infrastructure/api/client';
import { errorText } from '@infrastructure/api/errors';
import { HttpCustomerRepository } from '@infrastructure/api/HttpCustomerRepository';

import { Modal } from './Modal';

const repo = new HttpCustomerRepository(apiClient);

interface Props {
  open: boolean;
  customer: CustomerDetail | null;
  onClose: () => void;
  onSaved: (c: CustomerDetail) => void;
}

const FIELDS: { key: keyof CustomerEditableFields; label: string; type?: string }[] = [
  { key: 'company_name', label: 'Company name' },
  { key: 'contact_person', label: 'Contact person' },
  { key: 'phone', label: 'Phone' },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'country', label: 'Country' },
  { key: 'business_type', label: 'Business type' },
  { key: 'website', label: 'Website', type: 'url' },
  { key: 'sender_id', label: 'Sender ID' },
  { key: 'timezone', label: 'Timezone' },
  { key: 'webhook_url', label: 'Webhook URL', type: 'url' },
];

export function CustomerEditModal({ open, customer, onClose, onSaved }: Props) {
  const [vals, setVals] = useState<CustomerEditableFields>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!customer) return null;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    // Only send fields that differ from the current value.
    const diff: CustomerEditableFields = {};
    for (const [k, v] of Object.entries(vals)) {
      if (v === undefined) continue;
      const current = (customer as unknown as Record<string, string>)[k];
      if (v !== current) (diff as Record<string, string>)[k] = v;
    }
    try {
      const updated = await editCustomer(repo)(customer.id, diff);
      onSaved(updated);
      onClose();
      setVals({});
    } catch (e) {
      setError(
        e instanceof InvalidCustomerEdit ? e.message : errorText(e),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} title="Edit customer details" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-2 text-sm">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {FIELDS.map(({ key, label, type = 'text' }) => (
            <label key={key} className="block">
              {label}
              <input
                type={type}
                defaultValue={
                  (customer as unknown as Record<string, string>)[key] ?? ''
                }
                onChange={(e) =>
                  setVals((v) => ({ ...v, [key]: e.target.value }))
                }
                className="mt-1 w-full rounded border px-2 py-1"
              />
            </label>
          ))}
        </div>
        {error && <p className="text-red-700">{error}</p>}
        <div className="flex justify-end gap-2 pt-3">
          <button type="button" onClick={onClose} className="rounded border px-3 py-1">
            Cancel
          </button>
          <button
            type="submit" disabled={submitting}
            className="rounded bg-blue-600 px-3 py-1 text-white disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
